import { KN_GEOMETRY_TOLERANCE_PCT } from '@reos/config';
import type { R3FixtureFact } from '@reos/duediligence';
import { t } from '@reos/i18n';
import { reportClientError } from './client-error-reporter.js';

/**
 * Krok M1 — panel parcely (vpravo, záložka geometrie): řádky "z našich
 * faktů" se stavem důvěry a odkazem na evidence id (brief bod 3). Čistá
 * extrakce z `detail.facts` - žádné IO, žádné vymýšlení chybějících hodnot
 * ("Nedoplňuj mezery fantazií", CLAUDE.md §8): chybějící fakt = řádek se
 * `status: null` a `value: null`, UI ho vykreslí jako "nezjištěno".
 *
 * Fact keys jsou stejné jako v adaptérech (`ruian-vdp.ts`, `bpej.ts`,
 * `radon.ts`, `flood.ts`) - žádný nový fakt se tu nevymýšlí, jen se čte.
 */

export type ParcelPanelRowKey =
  | 'parcelLabel'
  | 'landType'
  | 'landUse'
  | 'areaKn'
  | 'areaGeometry'
  | 'areaMatch'
  | 'protection'
  // Krok KN1: zapis z API katastru (ČÚZK) - cislo LV, zpusoby ochrany a BPEJ.
  | 'lvNumber'
  | 'knProtection'
  | 'bpej'
  | 'radon'
  | 'flood'
  | 'accessRoad'
  | 'upFunction'
  | 'building'
  | 'municipality';

export interface ParcelPanelRow {
  readonly key: ParcelPanelRowKey;
  /** null = fakt v `detail.facts` chybi - UI vykresli "nezjisteno", ne vymyslenou hodnotu. */
  readonly value: string | null;
  readonly status: string | null;
  readonly factId: string | null;
}

function findFact(facts: readonly R3FixtureFact[], predicate: (fact: R3FixtureFact) => boolean): R3FixtureFact | null {
  return facts.find(predicate) ?? null;
}

function byKeyPrefix(facts: readonly R3FixtureFact[], prefix: string): R3FixtureFact | null {
  return findFact(facts, (f) => f.factKey === prefix || f.factKey.startsWith(`${prefix}#`));
}

function emptyRow(key: ParcelPanelRowKey): ParcelPanelRow {
  return { key, value: null, status: null, factId: null };
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/* ---------------------------------------------------- hodnota radku jako TEXT
 *
 * Krok QAFIX10 (nalez QA-ZAV-N2) — V PANELU ANI V EXPORTU NIKDY SUROVY JSON.
 *
 * Fakta behu nesou OBJEKTY (`planning.up_status` ma v Úmyslovicích 18 poli
 * vcetne `planEupCode`, `regionLayerId` a odkazu do evidence UPD). Radky
 * panelu tyhle objekty predavaly dal jako `JSON.stringify(...)`, takze:
 *
 *   1. makler cetl na obrazovce strojovy zapis misto odpovedi,
 *   2. „PDF prehledu parcely" (MAP3b `J1`) padalo na `400`
 *      (`/rows/13/value too_big`) — hodnota mela pres 900 znaku.
 *
 * Formatovani je TADY, u radku, ne v `rowDisplayValue` v `ParcelPanel.tsx`:
 * v komponente uz je hodnota retezec, zatimco tvar faktu zije jen tady —
 * a export posila PRESNE radky panelu (vzor O5b), takze jedna oprava spravi
 * obrazovku i dokument.
 *
 * MISTNI KOPIE SEMANTIKY, ne import: bohatsi stav uzemniho planu zna
 * `upPlanStateFrom` (`@reos/duediligence` → `internal/report/v5/site-facts.
 * ts`) a stupen zaplav `floodZoneRiskLevel` (`public/flood-risk.ts`) —
 * prohlizecovy bundle z toho balicku smi brat JEN TYPY
 * (`test/browser-safe-imports.spec.ts`), hodnotovy import shodi `vite build`.
 * ODLISNOST JE ZAMERNA: panel tiskne JEDNU RADKU do tabulky („Plán známe,
 * plochu doplní člověk"), ne stav se sesti polemi pro kapitolu reportu;
 * cteny tvar faktu (`functionalAreaCode`, `planName`, `present`, `reason`)
 * je ale tentyz a slova zaplav jsou tataz trojice jako v dokumentu
 * („zasahuje / mimo / nezjištěno", `documents/interni-audit.ts`).
 */

type FactRecord = Readonly<Record<string, unknown>>;

/** Panel mluvi cesky stejne jako `ParcelPanel.tsx` (`locale: 'cs'`). */
function text(key: Parameters<typeof t>[0], values?: Record<string, string>): string {
  return t(key, { locale: 'cs', ...(values === undefined ? {} : { values }) });
}

function record(value: unknown): FactRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as FactRecord)
    : null;
}

/** „Radonový index: nízký" — stupen z geologie (adapter `radon.ts`). */
function radonValue(value: unknown): string | null {
  const index = str(record(value)?.['radonIndex']);
  return index === null ? null : text('webAdmin.parcelMap.panel.value.radon', { index });
}

/** „Záplavy Q100: mimo" — dva pravopisy tehoz faktu (`present`, `inFloodZone`). */
function floodValue(value: unknown): string | null {
  const row = record(value);
  if (row === null) return null;
  const inZone = typeof row['inFloodZone'] === 'boolean' ? row['inFloodZone'] : row['present'];
  if (typeof inZone !== 'boolean') return null;
  return text(
    inZone ? 'webAdmin.parcelMap.panel.value.floodInside' : 'webAdmin.parcelMap.panel.value.floodOutside',
  );
}

/** „Přístup z veřejné komunikace: ano (silnice)" (adapter `access.public_road`). */
function accessRoadValue(value: unknown): string | null {
  const row = record(value);
  if (row === null) return null;
  const access = row['value'];
  if (typeof access !== 'boolean') return null;
  if (!access) return text('webAdmin.parcelMap.panel.value.accessRoadNo');
  const landUse = str(row['landUse']);
  return landUse === null
    ? text('webAdmin.parcelMap.panel.value.accessRoadYes')
    : text('webAdmin.parcelMap.panel.value.accessRoadYesLandUse', { landUse });
}

/**
 * Funkce dle UP: kod plochy, kdyz ho mame; jinak odpoved „plán známe /
 * obec plán nemá" (poradi i tvary podle `upPlanStateFrom`, viz vys).
 */
function upFunctionValue(value: unknown): string | null {
  const row = record(value);
  if (row === null) return null;
  const area = str(row['functionalAreaCode']);
  if (area !== null) return area;
  if (row['present'] === false || str(row['reason']) === 'up_not_issued') {
    return text('webAdmin.parcelMap.panel.value.upNotIssued');
  }
  const planName = str(row['planName']) ?? str(row['baseDocument']);
  const documents = Array.isArray(row['documents']) ? row['documents'] : [];
  const known =
    planName !== null ||
    documents.length > 0 ||
    row['present'] === true ||
    row['hasEffectivePlan'] === true ||
    str(row['status']) === 'platny';
  if (!known) return null;
  return planName === null
    ? text('webAdmin.parcelMap.panel.value.upPlanOnly')
    : text('webAdmin.parcelMap.panel.value.upPlanNamed', { plan: planName });
}

const ROW_VALUE_FORMATTER: Partial<Record<ParcelPanelRowKey, (value: unknown) => string | null>> = {
  radon: radonValue,
  flood: floodValue,
  accessRoad: accessRoadValue,
  upFunction: upFunctionValue,
};

/**
 * Hodnota radku jako text — POJISTKA pro vsechny klice bez vlastniho
 * formatovani: retezec a cislo projdou, cokoli jineho je `null` („—") a
 * zapise se jako `ui_error` do `/v1/client-errors` (s KLICEM FAKTU, nikdy
 * s hodnotou — ta muze nest PII). Tise vratit JSON uz nejde: makler by cetl
 * strojovy zapis a export by na nem padal.
 */
export function parcelRowValueText(
  key: ParcelPanelRowKey,
  factKey: string,
  value: unknown,
): string | null {
  const formatter = ROW_VALUE_FORMATTER[key];
  if (formatter !== undefined) return formatter(value);
  const plain = str(value) ?? (num(value) === null ? null : String(num(value)));
  if (plain !== null) return plain;
  if (value === null || value === undefined) return null;
  reportClientError({
    kind: 'ui_error',
    message: `parcel-panel: neznamy tvar hodnoty faktu ${factKey} (radek ${key})`,
  });
  return null;
}

/**
 * "Shoda" kdyz se zapis v katastru a tvar parcely lisi o min nez
 * `KN_GEOMETRY_TOLERANCE_PCT`, jinak rozdil v m2.
 *
 * Krok MRGR1, nalez N4 — JEDNA MEZ PRO PANEL I REPORT. Panel mel vlastni
 * `AREA_MATCH_TOLERANCE_PCT = 2` merenou vuci ZAPISU, zatimco beh i report
 * meri 3 % vuci GEOMETRII (`compareAreas` v @reos/duediligence) — parcela
 * s rozdilem 2,5 % tak byla v panelu "nesedi" a v reportu "sedi". Mez je od
 * MRGR1 sdilena (`@reos/config`) a referenci je stejne jako v `compareAreas`
 * VZDY geometrie.
 *
 * Proc tu je vlastni ctyrradkovy vypocet a ne volani `compareAreas`: ta zije
 * v @reos/duediligence, odkud smi prohlizecovy bundle brat jen TYPY
 * (`test/browser-safe-imports.spec.ts`). Parita je v
 * `parcel-panel-rows.spec.ts`, oddil "N4" — mez se tam bere z teze konstanty,
 * takze cislo nemuze znovu odejit samo.
 */
function areaMatchValue(areaKnM2: number | null, geometryAreaM2: number | null): string | null {
  if (areaKnM2 === null || geometryAreaM2 === null) return null;
  const diff = Math.abs(areaKnM2 - geometryAreaM2);
  const withinTolerance = geometryAreaM2 <= 0 ? diff === 0 : (diff / geometryAreaM2) * 100 <= KN_GEOMETRY_TOLERANCE_PCT;
  return withinTolerance ? 'match' : `mismatch:${diff.toFixed(1)}`;
}

/**
 * Krok KN1 — KLICE FAKTU Z API KATASTRU.
 *
 * Jsou tu jako literaly ze stejneho duvodu, jako zbytek tohohle souboru
 * (`environment.zpf_protection_class`, `parcel.identity`): prohlizecovy
 * bundle smi z `@reos/duediligence` brat JEN TYPY (`test/browser-safe-imports.
 * spec.ts`), ne hodnoty. SSOT je `internal/apikn/facts.ts`
 * (`KN_LV_FACT_KEY`, `KN_PROTECTION_FACT_KEY`, `KN_BPEJ_FACT_KEY`,
 * `PLOMBA_FACT_KEY`); parita se meri v `parcel-panel-rows.spec.ts`.
 */
const KN_LV_FACT_KEY = 'land_registry.lv_number';
const KN_PROTECTION_FACT_KEY = 'land_registry.protection';
const KN_BPEJ_FACT_KEY = 'land_registry.bpej';
const PLOMBA_FACT_KEY = 'ownership.plomba';

/**
 * Krok KN4 bod 2 — RADKY, KTERE NESOU ZAPIS V KATASTRU.
 *
 * § 53 katastralniho zakona: udaje katastru se smi sirit jen se souhlasem
 * ČÚZK. Dokud ho nemame (`KN_OUTPUTS_ENABLED=false`, flag `knOutputs`
 * z `/v1/me`), panel tyhle radky VUBEC NEUKAZE — ani jako „nezjištěno", to by
 * slibovalo, ze je doplnime. Misto nich je tlacitko „Zobrazit v katastru".
 *
 * Priznak u KLICU RADKU, ne filtrace faktu: panel cte `detail.facts`, kde
 * fakt z KN uz byt muze (zakazka z doby pred KN4) — ale ukazat ho nesmi.
 * SSOT pravidla je `@reos/duediligence` → `public/kn-output-policy.ts`;
 * prohlizecovy bundle z nej smi brat jen TYPY (`test/browser-safe-imports.
 * spec.ts`), takze parita se meri v `parcel-panel-rows.spec.ts`.
 */
const KN_ROW_KEYS: readonly ParcelPanelRowKey[] = ['lvNumber', 'knProtection', 'bpej'];

/** Odkaz na vyhledavaci stranku parcely v Nahlizeni do KN (viz politika). */
export const NAHLIZENI_DO_KN_PARCEL_URL =
  'https://nahlizenidokn.cuzk.gov.cz/VyberParcelu/Parcela/InformaceO';

/**
 * Krok KNT1 bod 2 — VODOZNAK INTERNIHO TESTU NA OBRAZOVCE.
 *
 * Kdyz udaje katastru vidi makler jen pres vyjimku nasi interni testovaci
 * kancelare (flag `knOutputsTest` z `/api/me`), musi na panelu videt, ze je
 * sirit nesmi — snimek obrazovky se rozesila stejne snadno jako PDF.
 *
 * MISTNI KOPIE, ne import: SSOT je `KN_TEST_WATERMARK`
 * v `@reos/duediligence` → `public/kn-output-policy.ts`, ale prohlizecovy
 * bundle z toho balicku smi brat jen TYPY (`test/browser-safe-imports.
 * spec.ts`) — hodnotovy import shodi `vite build`. Paritu proti SSOT meri
 * `parcel-panel-rows.spec.ts`, stejne jako u `KN_ROW_KEYS` vys.
 */
export const KN_TEST_WATERMARK = 'INTERNÍ TEST — údaje z katastru, nešířit';

function lvNumberValue(value: unknown): string | null {
  const lv = num((value as { readonly lv_number?: unknown } | undefined)?.lv_number);
  return lv === null ? null : String(lv);
}

/** Prazdny seznam ochran je ODPOVED („bez zápisu"), ne chybejici udaj. */
function knProtectionValue(value: unknown): string | null {
  const raw = (value as { readonly protections?: unknown } | undefined)?.protections;
  if (!Array.isArray(raw)) return null;
  const names = raw.map((row) => str((row as { readonly name?: unknown } | null)?.name)).filter((n): n is string => n !== null);
  return names.length === 0 ? 'none' : names.join(', ');
}

function bpejValue(value: unknown): string | null {
  const raw = (value as { readonly bpej?: unknown } | undefined)?.bpej;
  if (!Array.isArray(raw)) return null;
  const codes = raw
    .map((row) => num((row as { readonly code?: unknown } | null)?.code))
    .filter((c): c is number => c !== null)
    .map((c) => String(c));
  return codes.length === 0 ? null : codes.join(', ');
}

/** Jedno bezici rizeni (plomba) tak, jak ho panel ukaze. */
export interface PlombaProceeding {
  /** `V-1234/2026` - tvar z Nahlizeni do KN. */
  readonly label: string;
  readonly receivedAt: string | null;
}

export interface PlombaWarning {
  readonly proceedings: readonly PlombaProceeding[];
  /** `aktualnostDatK` z API KN - „stav k ...". */
  readonly dataAsOf: string | null;
  readonly factId: string | null;
}

/**
 * Krok KN1 bod 3 — VYRAZNE UPOZORNENI „na nemovitosti probíhá řízení".
 *
 * `null` znamena „plomba tu neni, nebo o ni nevime" - tedy zadne upozorneni.
 * Panel nesmi mlcet jen proto, ze fakt chybi, ale taky nesmi strasit
 * upozornenim, pro ktere neni podklad: proto se bere VYHRADNE fakt
 * `ownership.plomba` s neprazdnym seznamem rizeni.
 */
export function plombaWarningFromFacts(
  facts: readonly R3FixtureFact[],
  parcelLabel?: string | null,
  /** Krok KN4: pri vypnutem sireni udaju KN upozorneni nevznika (viz `KN_ROW_KEYS`). */
  knOutputsEnabled = false,
): PlombaWarning | null {
  if (!knOutputsEnabled) return null;
  const fact =
    (parcelLabel == null ? null : byKeyPrefix(facts, `${PLOMBA_FACT_KEY}#${parcelLabel}`)) ??
    byKeyPrefix(facts, PLOMBA_FACT_KEY);
  if (fact === null) return null;
  const value = fact.value as
    | { readonly plomba?: unknown; readonly proceedings?: unknown; readonly data_as_of?: unknown }
    | undefined;
  if (value?.plomba !== true) return null;
  const rows = Array.isArray(value.proceedings) ? value.proceedings : [];
  const proceedings = rows
    .map((row) => {
      const item = row as { readonly label?: unknown; readonly received_at?: unknown } | null;
      const label = str(item?.label);
      return label === null ? null : { label, receivedAt: str(item?.received_at) };
    })
    .filter((row): row is PlombaProceeding => row !== null);
  if (proceedings.length === 0) return null;
  return { proceedings, dataAsOf: str(value.data_as_of), factId: fact.id };
}

export function buildParcelPanelRows(
  facts: readonly R3FixtureFact[],
  geometryAreaM2: number | null,
  /** Krok KN4: `false` = radky se zapisem v katastru se vubec nevykresli. */
  knOutputsEnabled = false,
): readonly ParcelPanelRow[] {
  const identity = byKeyPrefix(facts, 'parcel.identity');
  const identityValue = identity?.value as
    | { readonly label?: string; readonly landType?: string | null; readonly landUse?: string | null }
    | undefined;
  const parcelLabel = str(identityValue?.label);

  const areaKn = parcelLabel === null ? null : byKeyPrefix(facts, `parcel.area.kn_m2#${parcelLabel}`);
  const areaKnM2 = num((areaKn?.value as { readonly areaM2?: unknown } | undefined)?.areaM2);

  const protection = parcelLabel === null ? null : byKeyPrefix(facts, `environment.zpf_protection_class#${parcelLabel}`);
  const lvNumber = parcelLabel === null ? null : byKeyPrefix(facts, `${KN_LV_FACT_KEY}#${parcelLabel}`);
  const knProtection = parcelLabel === null ? null : byKeyPrefix(facts, `${KN_PROTECTION_FACT_KEY}#${parcelLabel}`);
  const bpej = parcelLabel === null ? null : byKeyPrefix(facts, `${KN_BPEJ_FACT_KEY}#${parcelLabel}`);
  const radon = parcelLabel === null ? null : byKeyPrefix(facts, `environment.radon_index#${parcelLabel}`);
  const flood = parcelLabel === null ? null : byKeyPrefix(facts, `environment.flood.q100#${parcelLabel}`);
  const accessRoad = byKeyPrefix(facts, 'access.public_road');
  /*
   * Krok QAFIX10: `planning.up_status` zapisuji CTYRI cesty (mapovy portal,
   * jednotny standard, krajska sluzba, evidence UPD) a kazda jinym tvarem —
   * u Lysé nad Labem je prvni v poradi fakt mapoveho portalu
   * (`outside_map_portal_coverage`), ktery o planu nerika nic, zatimco
   * evidence UPD zna jeho jmeno. Proto se bere PRVNI FAKT, ktery odpovida;
   * je to tentyz zakon jako `identifiesPlan` v `upPlanStateFrom`
   * (@reos/duediligence) — veta patri zdroji, ktery odpovedel.
   */
  const upStatusFacts = facts.filter(
    (f) => f.factKey === 'planning.up_status' || f.factKey.startsWith('planning.up_status#'),
  );
  const upFunction =
    upStatusFacts.find((f) => upFunctionValue(f.value) !== null) ?? upStatusFacts[0] ?? null;
  const building = byKeyPrefix(facts, 'building.status');
  const address = byKeyPrefix(facts, 'address.ruian.code');

  const rows: ParcelPanelRow[] = [
    identity !== null
      ? { key: 'parcelLabel', value: parcelLabel, status: identity.status, factId: identity.id }
      : emptyRow('parcelLabel'),
    identity !== null
      ? { key: 'landType', value: str(identityValue?.landType), status: identity.status, factId: identity.id }
      : emptyRow('landType'),
    identity !== null
      ? { key: 'landUse', value: str(identityValue?.landUse), status: identity.status, factId: identity.id }
      : emptyRow('landUse'),
    areaKn !== null
      ? { key: 'areaKn', value: areaKnM2 === null ? null : String(areaKnM2), status: areaKn.status, factId: areaKn.id }
      : emptyRow('areaKn'),
    {
      key: 'areaGeometry',
      value: geometryAreaM2 === null ? null : String(geometryAreaM2),
      status: geometryAreaM2 === null ? null : 'VERIFIED',
      factId: null,
    },
    { key: 'areaMatch', value: areaMatchValue(areaKnM2, geometryAreaM2), status: null, factId: null },
    protection !== null
      ? {
          key: 'protection',
          value: parcelRowValueText('protection', protection.factKey, protection.value),
          status: protection.status,
          factId: protection.id,
        }
      : emptyRow('protection'),
    lvNumber !== null
      ? {
          key: 'lvNumber',
          value: lvNumberValue(lvNumber.value),
          status: lvNumber.status,
          factId: lvNumber.id,
        }
      : emptyRow('lvNumber'),
    knProtection !== null
      ? {
          key: 'knProtection',
          value: knProtectionValue(knProtection.value),
          status: knProtection.status,
          factId: knProtection.id,
        }
      : emptyRow('knProtection'),
    bpej !== null
      ? { key: 'bpej', value: bpejValue(bpej.value), status: bpej.status, factId: bpej.id }
      : emptyRow('bpej'),
    radon !== null
      ? {
          key: 'radon',
          value: parcelRowValueText('radon', radon.factKey, radon.value),
          status: radon.status,
          factId: radon.id,
        }
      : emptyRow('radon'),
    flood !== null
      ? {
          key: 'flood',
          value: parcelRowValueText('flood', flood.factKey, flood.value),
          status: flood.status,
          factId: flood.id,
        }
      : emptyRow('flood'),
    accessRoad !== null
      ? {
          key: 'accessRoad',
          value: parcelRowValueText('accessRoad', accessRoad.factKey, accessRoad.value),
          status: accessRoad.status,
          factId: accessRoad.id,
        }
      : emptyRow('accessRoad'),
    upFunction !== null
      ? {
          key: 'upFunction',
          value: parcelRowValueText('upFunction', upFunction.factKey, upFunction.value),
          status: upFunction.status,
          factId: upFunction.id,
        }
      : emptyRow('upFunction'),
    building !== null
      ? {
          key: 'building',
          value: parcelRowValueText('building', building.factKey, building.value),
          status: building.status,
          factId: building.id,
        }
      : emptyRow('building'),
    address !== null
      ? {
          key: 'municipality',
          value: str((address.value as { readonly formatted?: unknown } | undefined)?.formatted),
          status: address.status,
          factId: address.id,
        }
      : emptyRow('municipality'),
  ];
  return knOutputsEnabled ? rows : rows.filter((row) => !KN_ROW_KEYS.includes(row.key));
}

export interface MunicipalityWebsiteChip {
  readonly url: string;
  readonly factId: string | null;
}

/**
 * M1b — chip "Web obce": cte fakt `municipality.website` (adapter Seznam
 * OVM, `@reos/duediligence`). `null` kdyz fakt chybi (obec bez zdroje) NEBO
 * kdyz adapter zaznamenal `NOT_FOUND` - chip pak zobrazi duvod, ne rozbity
 * odkaz (T1b).
 */
export function municipalityWebsiteFromFacts(facts: readonly R3FixtureFact[]): MunicipalityWebsiteChip | null {
  const fact = byKeyPrefix(facts, 'municipality.website');
  if (fact === null) return null;
  const url = str((fact.value as { readonly url?: unknown } | undefined)?.url);
  return url === null ? null : { url, factId: fact.id };
}
